import React, { useLayoutEffect, useRef, useState } from 'react';
import ResizeDetector from 'react-resize-detector';
import classNames from 'classnames';
import { LinearProgress } from '@mui/material';
import { CommentComponent } from './CommentComponent';
import { InputField } from './InputField';
import { containsNewComment, containsOlderComment, isMyComment } from '../utils';
import AccurityPaper from '../../paper/components/AccurityPaper';
import { useIsScrolledUp } from '../hooks/isScrolledUpHook';
import { useScrollManager } from '../hooks/scrollManagerHook';
import { useIsScrollBarVisible } from '../hooks/isScrollBarVisibleHook';
import AccurityIconButton from '../../buttons/components/AccurityIconButton';
var ticking = false;
export var CommentsContainer = function (_a) {
    var comments = _a.comments, onCreate = _a.onCreate, onDelete = _a.onDelete, onSave = _a.onSave, onLoadMore = _a.onLoadMore, totalCount = _a.totalCount, onClose = _a.onClose, isLoading = _a.isLoading, displayNotificationToggle = _a.displayNotificationToggle, isNotificationActive = _a.isNotificationActive, isNotificationLoading = _a.isNotificationLoading, onNotificationClick = _a.onNotificationClick, user = _a.user;
    var _b = useState(false), newCommentAvailable = _b[0], setNewCommentAvailable = _b[1];
    var _c = useState(0), previousScrollHeight = _c[0], setPreviousScrollHeight = _c[1];
    var _d = useState([]), previousComments = _d[0], setPreviousComments = _d[1];
    var listRef = useRef(null);
    var _e = useIsScrolledUp(listRef), isScrolledUp = _e[0], updateIsScrolledUp = _e[1];
    var _f = useIsScrollBarVisible(listRef), isScrollBarVisible = _f[0], updateIsScrollBarVisible = _f[1];
    var _g = useScrollManager(listRef), scrollDown = _g.scrollDown, preserveScrollPosition = _g.preserveScrollPosition, onEdgeReached = _g.onEdgeReached;
    var handleScroll = function () {
        if (!ticking) {
            requestAnimationFrame(function () {
                updateIsScrolledUp();
                var hideNewCommentMessage = function () {
                    setNewCommentAvailable(false);
                    scrollDown('smooth');
                };
                onEdgeReached(function () { return totalCount > comments.length && !isLoading && onLoadMore(); }, // load previous comments when we reach top edge
                function () { return newCommentAvailable && hideNewCommentMessage(); } // hide message about new comment when we reach bottom
                );
                ticking = false;
            });
            ticking = true;
        }
    };
    var handleListHeightChange = function () {
        // when list's height was changed, we want to keep user scrolled down
        // if he is scrolled up, we don't want to disturb him by scrolling down
        if (!isScrolledUp) {
            scrollDown();
        }
        updateIsScrollBarVisible();
    };
    useLayoutEffect(function () {
        var _a, _b;
        // scroll down comments on first render if there are more comments than it's possible to display
        var scrollHeight = ((_a = listRef.current) === null || _a === void 0 ? void 0 : _a.scrollHeight) || 0;
        var clientHeight = ((_b = listRef.current) === null || _b === void 0 ? void 0 : _b.clientHeight) || 0;
        if (previousComments.length === 0 && comments.length > 0 && scrollHeight > clientHeight) {
            setTimeout(function () { return scrollDown(); }, 0);
        }
        // when comments change, we want to react to potential changes in height of the list due to CRUD operation on comment
        handleListHeightChange();
        // show message about new comment if user is scrolled up
        if (isScrolledUp) {
            setNewCommentAvailable(containsNewComment(previousComments, comments));
        }
        // when we load previous set of comments, we want to preserve scroll position to prevent a list to jump
        if (containsOlderComment(previousComments, comments)) {
            preserveScrollPosition(previousScrollHeight);
        }
        // previous scroll height needs to set in next app loop, because we have to get height when the messages and input field are in read/empty state
        setTimeout(function () { var _a; return setPreviousScrollHeight(((_a = listRef.current) === null || _a === void 0 ? void 0 : _a.scrollHeight) || 0); }, 1);
        // update previous comments in order to use the value in next update loop
        setPreviousComments(comments);
    }, [comments]);
    // order comments by created date (newest on the bottom)
    comments = comments.sort(function (a, b) { return a.createdDate - b.createdDate; });
    var toggleNotificationsTitle = isNotificationActive ? 'Toggle comment notifications OFF' : 'Toggle comment notifications ON';
    if (isNotificationLoading) {
        toggleNotificationsTitle = 'Action in progress';
    }
    return (React.createElement(AccurityPaper, { className: classNames('commentsContainer'), elevation: 2 },
        React.createElement("h3", { className: 'commentsContainer__title' },
            React.createElement("span", null, "Comments"),
            React.createElement("div", { className: 'commentsContainer__topBar' },
                displayNotificationToggle &&
                    React.createElement(AccurityIconButton, { iconName: isNotificationActive ? 'mui:NotificationsOffOutlined' : 'mui:NotificationsOutlined', title: toggleNotificationsTitle, onClick: onNotificationClick, disabled: isNotificationLoading, size: "small" }),
                React.createElement(AccurityIconButton, { iconName: "mui:Close", onClick: onClose, size: "small" }))),
        isLoading && React.createElement(LinearProgress, { className: 'commentsContainer__loadingIndicator' }),
        React.createElement(ResizeDetector, { handleHeight: true, onResize: handleListHeightChange, targetDomEl: listRef.current || undefined },
            React.createElement("div", { className: classNames('commentsList', {
                    'commentsList--withScrollBar': isScrollBarVisible
                }), ref: listRef, onScroll: handleScroll }, comments.map(function (comment) {
                return (React.createElement(CommentComponent, { key: comment.id, comment: comment, isMyComment: isMyComment(user, comment), onDelete: onDelete, onSave: onSave }));
            }))),
        newCommentAvailable && React.createElement("div", { className: "newCommentsMessage", onClick: function () { return scrollDown('smooth'); }, dangerouslySetInnerHTML: { __html: 'New Comment &DownArrowBar;' } }),
        React.createElement("div", { className: "commentInputField" },
            React.createElement(InputField, { onSave: onCreate }))));
};
//# sourceMappingURL=CommentsContainer.js.map